import { useTracking } from 'react-tracking';
import { SnowflakeType } from '@/components/DeprecatedSnowflake/SnowflakeType';
import { Button, Row, Column } from '@simplywallst/ui-core';
import { ReturnsSummaryContainer } from '@/components/DeprecatedPortfolio/components/ReturnsSummary';
import ReportSection from '@/components/ReportSection';
import { ExecutiveSummaryIntroduction } from '@/components/DeprecatedPortfolio/components/Summary';
import { EditPortfolioModalContainer } from '@/components/DeprecatedPortfolio/components/EditPortfolioModal';
import {
  PORTFOLIO_STATIC_COMPANY,
  PORTFOLIO_SUMMARY,
} from '@/constants/feedbackSections';

import Media from 'react-media';
import { useTheme } from '@simplywallst/ui-core';

import {
  SnowflakeWrapper,
  SummaryWrapper,
  ColumnLeft,
  ActionsWrapper,
  HideLgDown,
  SummarySubHead,
} from './styled';

import {
  COMPANY_VALUE_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
} from '@/constants/anchors';
import loadable from '@loadable/component';
import { SectionTitle } from '@/components/ReportSectionHeader';

const Snowflake = loadable(() => import('@/components/DeprecatedSnowflake'));

const SnowflakeActions = loadable(
  () => import('@/components/DeprecatedSnowflake/SnowflakeActions')
);
const routes: string[] = [
  COMPANY_VALUE_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
];

export interface SummaryProps {
  isPublic?: boolean;
  sectionNumber: number;
  annualReturn: number;
  annualReturnFormatted: string;
  currencyISO: string;
  currentHoldingCount: number;
  totalDividendReturnFormatted: string;
  isWatchlist: boolean;
  portfolioYearAge: number;
  portfolioWithNoTransactions: boolean;
  portfolioId: string;
  portfolioName: string;
  snowflakeDescription: string;
  snowflakeScoreAxes: [number, number, number, number, number];
  secretCode?: string;
  redirectToPortfolioReturnsReport: (args: {
    id: string;
    name: string;
  }) => void;
}

const Summary = ({
  isPublic,
  sectionNumber,
  annualReturn,
  annualReturnFormatted,
  currencyISO,
  currentHoldingCount,
  totalDividendReturnFormatted,
  isWatchlist,
  portfolioYearAge,
  portfolioWithNoTransactions,
  portfolioId,
  portfolioName,
  snowflakeDescription,
  snowflakeScoreAxes,
  redirectToPortfolioReturnsReport,
}: SummaryProps) => {
  const theme = useTheme();
  const { trackEvent } = useTracking();
  const handleLinkToReturnsReport = () => {
    redirectToPortfolioReturnsReport &&
      redirectToPortfolioReturnsReport({
        id: portfolioId,
        name: portfolioName,
      });
    trackEvent({
      subject: 'summary',
      action: 'click',
      modifier: 'Detailed Returns Report',
    });
  };

  return (
    <ReportSection sectionNumber={sectionNumber}>
      <SummaryWrapper>
        <Row>
          <ColumnLeft col={12} colLg={8}>
            <SectionTitle>Portfolio Summary</SectionTitle>
            <ExecutiveSummaryIntroduction
              currencyISO={currencyISO}
              isWatchlist={isWatchlist}
              annualReturn={annualReturn}
              portfolioName={portfolioName}
              portfolioYearAge={portfolioYearAge}
              annualReturnFormatted={annualReturnFormatted}
              portfolioWithNoTransactions={portfolioWithNoTransactions}
              totalDividendReturnFormatted={totalDividendReturnFormatted}
            />
          </ColumnLeft>
          <SnowflakeWrapper
            data-cy-id="portfolio-summary-snowflake"
            col={12}
            colLg={4}
          >
            <SummarySubHead>Portfolio Snowflake</SummarySubHead>
            <Row justifyContent="center">
              <Column col={12} colSm={8} colMd={6} colLg={12}>
                <Snowflake
                  points={snowflakeScoreAxes}
                  tooltip={snowflakeScoreAxes && snowflakeScoreAxes.length > 0}
                  routes={routes}
                  currentHoldingCount={currentHoldingCount}
                  type={
                    isWatchlist
                      ? SnowflakeType.WATCHLIST
                      : SnowflakeType.PORTFOLIO
                  }
                  handleClick={(route) => {
                    location.hash = route;
                  }}
                />
              </Column>
            </Row>
            <Row justifyContent="center">
              <Column col={12} colSm={8} colMd={6} colLg={12}>
                <SnowflakeActions
                  id="pf-summary-snowflake"
                  points={snowflakeScoreAxes}
                  companyForFeedback={PORTFOLIO_STATIC_COMPANY}
                  feedbackLabel={PORTFOLIO_SUMMARY}
                />
              </Column>
            </Row>
          </SnowflakeWrapper>
          <ActionsWrapper col={12} colLg={8}>
            <SummarySubHead>Fundamentals</SummarySubHead>
            <p>{snowflakeDescription}</p>
            {!isPublic ? (
              <>
                <Media query={{ minWidth: theme.lgMinRaw }}>
                  {(match) => {
                    return match && RUNTIME_ENV !== 'native' ? (
                      <>
                        <SummarySubHead>Actions</SummarySubHead>
                        <div>
                          <Button
                            styleType="louderLarge"
                            data-cy-id="portfolio-returns-report"
                            displaySmDown="block"
                            marginBottom
                            onClick={handleLinkToReturnsReport}
                          >
                            <>
                              <HideLgDown>Detailed </HideLgDown>
                              Returns Report
                            </>
                          </Button>
                        </div>
                      </>
                    ) : null;
                  }}
                </Media>
              </>
            ) : null}
          </ActionsWrapper>
        </Row>
        <hr />
        <ReturnsSummaryContainer />
      </SummaryWrapper>
      <EditPortfolioModalContainer />
    </ReportSection>
  );
};

export default Summary;
